import { TId } from '@apia/util';
import { identifiers, parseTOKS } from './common';
import { getLabel } from '../../../../../../../utils/util';

export const constants = Object.freeze({
  ...identifiers,
  deliverables: [
    {
      title: 'Project Charter',
      description:
        'A document that outlines the purpose, scope, objectives, stakeholders, and resources for the BPM project, serving as a formal authorization.',
    },
    {
      title: 'As-Is Process Documentation',
      description:
        'Detailed documentation of the current state of business processes, including flowcharts, descriptions, and metrics, providing a baseline for improvement.',
    },
    {
      title: 'Gap Analysis Report',
      description:
        'An analysis identifying gaps between the current and desired future state of business processes, highlighting inefficiencies and areas for improvement.',
    },
    {
      title: 'To-Be Process Design',
      description:
        'A detailed design of the future state of business processes, including redesigned process flowcharts and expected performance metrics, aimed at addressing identified gaps.',
    },
    {
      title: 'Business Case',
      description:
        'A document that justifies the investment in the BPM project, detailing expected benefits, costs, and return on investment.',
    },
    {
      title: 'Implementation Plan',
      description:
        'A comprehensive plan for executing the BPM project, including timelines, resources, roles and responsibilities, and milestones.',
    },
    {
      title: 'Change Management Plan',
      description:
        'A strategy for managing organizational change associated with the BPM project, including communication plans and training programs.',
    },
    {
      title: 'Technology Requirements Document',
      description:
        'A document outlining the technical requirements for supporting the redesigned processes, including software, hardware, and integrations.',
    },
    {
      title: 'Test Plan and Test Cases',
      description:
        'A plan for testing the redesigned processes, including test scenarios, expected outcomes, and criteria for success.',
    },
    {
      title: 'Training Materials',
      description:
        'Materials developed to train staff on the new processes, including manuals, guides, and online learning modules.',
    },
    {
      title: 'Project Status Reports',
      description:
        'Regular updates provided to stakeholders about the progress of the BPM project, including accomplishments, issues, and next steps.',
    },
    {
      title: 'Post-Implementation Review Report',
      description:
        'A document evaluating the success of the BPM project after implementation, including achieved benefits, performance metrics, and lessons learned.',
    },
  ],
  errors: {
    cannotImportConfigurationFile:
      'The given file cannot be processed as project configuration. Is it a valid configuration file?',
    cannotDownloadZipFile:
      "An error occurred while downloading the project's export file.",
    cannotImportObjectsFromApia:
      'An error occurred while importing elements from Apia',
    cannotOpenCollectionSelector:
      'An error occurred when opening the collection selector',
    cannotProcessZipFile: `An error occurred while processing zip documentation. Downloading default version.`,
    cannotRemoveAttributeDueToForeignKey: (...entitiesList: string[]) =>
      parseTOKS(
        `The current entity cannot be removed because it's being used in the foreign keys of the following entities: <ul><TOK1></ul>`,
        entitiesList.map((c) => `<li>${c}</li>`).join(''),
      ),
    cannotRemoveInstanceDueToDeps: (entityTitle: string, entityName: string) =>
      parseTOKS(
        `The instance cannot be removed because it's being used in the following entity instances: <TOK1> (<TOK2>) `,
        entityTitle,
        entityName,
      ),
    cannotUpdateAttributeDueToForeignKey: (...entitiesList: string[]) =>
      parseTOKS(
        `The current entity cannot be updated because it's being used in the foreign keys of the following entities: <ul><TOK1></ul>`,
        entitiesList.map((c) => `<li>${c}</li>`).join(''),
      ),
    elementAlreadyExists: () => 'The introduced name is already taken',
  },
  explanations: {
    docPricingLegend:
      'This document provides a comprehensive and easy-to-understand breakdown of project costs, meticulously detailing the objects being designed for further development, various team roles, essential activities, and timelines to ensure transparent and efficient financial planning.',
    excelActivitiesLegend:
      'The distribution of activities and roles is displayed below. \n\nThis information, used in conjunction with the activity schedule, helps to determine the assignment of assistants throughout the entire project.',
    excelItemsLegend: [
      'The complete list of items considered to be of interest to the client is displayed below.',
      'The document sent with the specification of processes and objects aims to explain the situation, in addition to good practices and general concepts.',
      'Indicators, Documents (document management), and various use cases, in particular, are not included in the scope and effort of the proposal.',
    ],
    excelPricingLegend:
      'This Excel sheet provides a comprehensive and easy-to-understand breakdown of project costs, meticulously detailing the objects being designed for further development, various team roles, essential activities, and timelines to ensure transparent and efficient financial planning.',
    excelScheduleLegend:
      'The timeline for each of the items of interest is below. Each column represents a period of 5 working days.',
    elementsWithSimilarities:
      "These buttons represent elements containing similar objects within the project's structure. A merge dialog will be displayed when clicking on any of them, facilitating the combination of their contents.",
    generateForm:
      'Introduce the form details, such as: form name, form title, form fields, fields types...',
    generateEntity:
      'Introduce the entity details, such as: entity name, entity title, entity fields, fields types...',
    generateProcess:
      'Introduce the process details, such as: process name, process title, tasks titles, tasks names, forms titles, forms names, fields titles, fields names, fields types...',
    naming:
      'This functionality allows determining how names will be assigned to different elements of the tree. Prefixes will be assigned to all elements. Suffixes will only be added to form fields, where the field type and attribute type will be evaluated to generate a composition of them: {PREFIX}_{FIELDNAME}_{ATTTYPE}_{FIELDTYPE}_[{GRID}?]',
    panelAutogenerated:
      "This panel was autogenerated due to the creation of its widget. You may refer to the widget's definition if you please.",
    searchSimilaritiesModal:
      'Please, choose a collection to search for similarities and specify the desired similarity threshold. The threshold represents the maximum allowable distance between two elements for them to be deemed similar.',
    templates:
      'These templates serve various functionalities by defining the documentation structure. They are formatted as interpolated markdown, which supports any valid markdown expression. You can rearrange the variables, denoted by {VARIABLE_NAME}, throughout the document to customize the layout according to your preferences.',
  },
  labels: {
    activity: () => 'Activity',
    activities: () => 'Activities',
    activitiesDefaults: () => 'Restore defaults',
    activitiesDistribution: () => 'Activities distribution',
    activitiesFix: () => 'Fix activities',
    activitiesProjectManagement: () => 'Project Management',
    activitiesSummary: () => 'Activities summary',
    activitiesTotal: () => 'Total (%)',
    activitiesTotalExplain: () =>
      'Each activity is distributed among the different roles, assigning a percentage to each of them. The total percentage must add up to 100%, otherwise the column will turn red indicating an error.',
    activitiesSurveyAndScope: () => 'Survey and Scope',
    activitiesPrototype: () => 'Prototype',
    activitiesPrototypeValidation: () => 'Prototype Validation',
    activitiesFunctionalImplementation: () => 'Functional Implementation',
    activitiesTechnicalImplementation: () => 'Technical Implementation',
    activitiesTesting: () => 'Testing',
    activitiesFinalAdjustments: () => 'Final Adjustments',
    activitiesDocumentation: () => 'Documentation',
    activitiesTraining: () => 'Training',
    activitiesInstallation: () => 'Installation',
    activitiesDeployment: () => 'Deployment',
    add: () => 'Add',
    addItem: (itemType: string) => parseTOKS(`Add <TOK1>`, itemType),
    all: () => 'All',
    alteration: () => 'Alteration',
    applyToCurrentProject: () => 'Add to project',
    attention: () => 'Attention',
    attribute: () => 'Attribute',
    attributes: () => 'Attributes',
    augmentForm: () => 'Augment form',
    augmentProcess: () => 'Augment process',
    augmentPrompt: () => 'Enhance prompt',
    binary: () => 'Preview', // gg
    bold: () => 'Bold',
    bookSheets: () => 'Book sheets',
    both: () => 'Both',
    businessClass: () => 'Business class',
    businessClasses: () => 'Business classes',
    button: () => 'Button',
    byAttType: () => 'By attribute type',
    byFieldType: () => 'By field type',
    cancel: () => 'Cancel',
    cancellation: () => 'Cancelation',
    cannotAggregate: () => 'This element cannot be aggregated',
    category: () => 'Category',
    caution: () => 'Caution',
    chat: () => 'Chat',
    checkbox: () => 'Checkbox',
    chooseExistingElement: () => 'Choose existing element',
    codeBlock: () => 'Code block',
    collection: () => 'Collection',
    combo: () => 'Combobox',
    combobox: () => 'Combobox',
    complexity: () => 'Complexity',
    complexityLow: () => 'Low',
    complexityMid: () => 'Medium',
    complexityHigh: () => 'High',
    complexityHours: () => 'Hours',
    complexityCustom: () => 'Custom',
    complexityFactors: () => 'Complexity factors',
    complexityFactorsAreas: () => 'Involved areas',
    complexityFactorsAreasTooltip: () =>
      'The number of functional areas, offices, groups, etc., using the system can potentially impact the overall complexity of the project by up to 20%.',
    complexityFactorsCounterpart: () => 'Skilled counterpart',
    complexityFactorsCounterpartTooltip: () =>
      'The counterpart may end up doing a significant part of the work or provide little support. They might be highly experienced in processes, organizational change, reengineering, or have no experience at all.',
    complexityFactorsIntegration: () => 'Integration',
    complexityFactorsIntegrationTooltip: () =>
      'The integration between processes generally introduces additional complexity to the project.',
    complexityFactorsKpis: () => 'KPIs',
    complexityFactorsKpisTooltip: () =>
      'Indicators increase the complexity of the system. A project with a high complexity in indicators can raise the overall complexity by up to 20%.',
    complexityFactorsOrganizationalMaturity: () => 'Organizational maturity',
    complexityFactorsOrganizationalMaturityTooltip: () =>
      "One factor that can affect the system's complexity is the level of conflict within the organization regarding the project and its ability to be flexible and adapt to changes.",
    complexityFactorsRate: () => 'Rate',
    complexityFactorsTeamExperience: () => 'Team experience',
    complexityFactorsTeamExperienceTooltip: () =>
      'One factor that can affect the productivity of system development is the experience level of the consulting team working on the project. It is advisable to have experienced professionals in APIA implementations in roles such as project management, functional consulting, and design and programming of business classes. Extensive experience refers to more than 2 years, while reasonable experience indicates more than 9 months.',
    complexityFactorsTecFunRelation: () => 'Tec/Func relation',
    complexityFactorsTecFunRelationTooltip: () =>
      "The ideal scenario is to maintain the technical/functional ratio close to 30% to avoid significant technical complexity, although this is not always manageable and depends on the project's nature. It should never exceed 50% in BPMS and is seldom found below 30%.",
    complexityFactorsUsers: () => 'Users ammount',
    complexityFactorsUsersTooltip: () =>
      'The number of users using the system can potentially impact the overall complexity of the project by up to 20%',
    configuration: () => 'Configuration',
    convertToBusinessClass: () => 'Convert to business class',
    clear: () => 'Clear',
    closed: () => 'Closed',
    companyIcon: () => 'Company icon',
    companyName: () => 'Company name',
    condition: () => 'Condition',
    content: () => 'Content',
    copy: () => 'Copy',
    copyItem: (name: string = '', type: string = '') =>
      parseTOKS('Copy <TOK1> <TOK2>', type, name),
    copyProject: () => 'Copy project',
    cost: () => 'Cost',
    costInDays: () => 'In days',
    costInMonths: () => 'In months',
    costByHour: () => 'By hour',
    costByProfile: () => 'By profile',
    costsHtml: () => 'Costs html',
    count: () => 'Count',
    createFunctionality: () => 'Create functionality',
    creation: () => 'Creation',
    cube: () => 'Cube',
    custom: () => 'Custom',
    dashboard: () => 'Dashboard',
    dashboardPanel: () => 'Panel',
    dashboardPanels: () => 'Panels',
    dashboards: () => 'Dashboards',
    dataOriginEntities: () => 'Data origin entities',
    dataType: () => 'Data type',
    date: () => 'Date',
    definition: () => 'Definition',
    delete: () => 'Delete',
    dependencies: () => 'Dependencies',
    description: () => 'Description',
    details: () => 'Details',
    deliverables: () => 'Deliverables',
    deliverablesAlreadyExists: () =>
      'There is already a deliverable with that name',
    deliverablesEdit: () => 'Edit deliverable',
    deliverablesNew: () => 'New deliverable',
    deselectAll: () => 'Deselect all',
    didNotAugmentPrompt: () =>
      "You are about to start a strict generation, but you haven't augmented the prompt. This might not be what you want. Are you sure you want to continue?",
    document: () => 'Document',
    documents: () => 'Documents',
    documentsForProcess: (proName: string) =>
      `Documents of the process <div>${proName}</div>`,
    downloadFormat: () => 'Download format',
    dshDefault: () => 'Default dashboard',
    dshMobileDefault: () => 'Default mobile dashboard',
    editable: () => 'Editable',
    editItemsTemplate: () => 'Edit items template',
    editSpecsTemplate: () => 'Edit specs template',
    effortByItem: () => 'Estimated effort by item type',
    enabled: () => 'Enabled',
    enableLocalCardinality: () => 'Enable local cardinality',
    entCreModalTitle: (entityName: string, entityTitle: string) =>
      parseTOKS(
        `Entity import configuration <TOK1> (<TOK2>)`,
        entityTitle,
        entityName,
      ),
    entCreTitle: (entityName: string, entityTitle: string) =>
      parseTOKS(
        'Import configuration for <TOK1> (<TOK2>)',
        entityTitle,
        entityName,
      ),
    entCreAdmType: () => 'Admin type',
    entCreAltPro: () => 'Alteration process',
    entCreChkCreation: () => 'Create entity',
    entCreComboAtt: () => 'Attribute to show in combo',
    entCreCreModal: () => 'Create modal',
    entCreEntName: () => 'Entity name',
    entCreEntPrefix: () => 'Entity prefix',
    entCreCrePro: () => 'Creation process',
    entCreFrm: () => 'Create form',
    entCreIgnorePK: () => 'Ignore primary keys',
    entCreIsCod: () => 'Code loading entity',
    entCreModalRetAtt: () => 'Attribute returned by modal',
    entCrePoolForProcesses: () => 'Pool for processes',
    entCreQry: () => 'Create query',
    entities: () => 'Entities',
    entity: () => 'Entity',
    entityInstance: () => 'Instance',
    entityInstances: () => 'Instances',
    entityInstancesCount: (n: number) => `${n} instances`,
    entityInstanceAttribute: () => 'Entity instance attribute',
    event: () => 'Event',
    events: () => 'Events',
    excel: () => 'Excel',
    executable: () => 'Executable',
    exportApiaObject: (itemName: string) =>
      parseTOKS(`Create in Apia: "<TOK1>"`, itemName),
    extended: () => 'Extended mode',
    externalInterface: () => 'External interface',
    externalInterfaces: () => 'External interfaces',
    field: () => 'Field',
    fields: () => 'Fields',
    fieldVisualization: () => 'Visualization',
    file: () => 'File',
    findSimilarities: () => 'Find similarities',
    foreignAttribute: () => 'Foreign attribute',
    foreignCardinality: () => 'Foreign cardinality',
    foreignEntity: () => 'Foreign entity',
    foreignKey: () => 'Foreign key',
    foreignKeys: () => 'Foreign keys',
    form: () => 'Form',
    formField: () => 'Field',
    formFields: () => 'Fields',
    formFromImage: () => 'From image',
    formGenerateSuccess: () => 'Form generated successfully',
    forms: () => 'Forms',
    showContextForms: () => 'Show context forms',
    free: () => 'Free',
    fromImage: () => 'From image',
    functionality: () => 'Functionality',
    generate: () => 'Generate',
    generateAdditionalData: () => 'Generate additional data',
    generateAdditionalDataPreloaded: () =>
      'Generate additional data (preloaded)',
    generateBoth: () => 'Generate both',
    generateEntity: () => 'Generate entity',
    generateForm: () => 'Generate form',
    generateForms: () => 'Generate forms',
    generateImage: () => 'Generate image',
    generateInstances: () => 'Generate instances',
    generateObject: () => 'Generate object',
    generateTasks: () => 'Generate tasks',
    generateProcess: () => 'Generate process',
    generateProcessFrom: (what: string) =>
      parseTOKS(`Generate process from <TOK1>`, what),
    generateWithStrictAttributes: () => 'Strict on attributes',
    generateWithStrictFields: () => 'Strict on fields',
    generateWithStrictTasks: () => 'Strict on tasks',
    getTabular: () => 'Get structured preview',
    goal: () => 'Goal',
    goBackToTask: (taskName: string) => `Back to ${taskName}`,
    goForwardToTask: (taskName: string) => `To ${taskName}`,
    gotoEndTask: () => 'Can go to end',
    gotoWhichTasks: () => 'What tasks goes to',
    gotoFlowConfiguration: () => 'Flow configuration',
    gotoTasksOrder: () => 'Tasks order',
    grid: () => 'Grid',
    height: () => 'Height',
    hidden: () => 'Hidden',
    hideFunctionality: () => 'Hide functionality',
    hours: () => 'Hours',
    html: () => 'Html',
    htmlCode: () => 'Html code',
    http: () => 'Http',
    iframe: () => 'Iframe',
    image: () => 'Image',
    imageDescription: () => 'Image description',
    images: () => 'Images',
    importApiaObject: () => 'Import from Apia',
    input: () => 'Input',
    internetSearch: () => 'Internet search',
    instance: () => 'Instance',
    instances: () => 'Instances',
    introduceFormDescription: () => 'Introduce form description',
    invisible: () => 'Invisible',
    isStartTask: () => 'Start task',
    items: () => 'Items',
    itemsSummary: () => 'Items summary',
    kpi: () => 'KPI',
    kpiMaxValue: () => 'KPI max value',
    kpiMinValue: () => 'KPI min value',
    kpiMultiplier: () => 'KPI multiplier',
    kpiMultiThousanth: () => 'Thousandth',
    kpiMultiHundredth: () => 'Hundredth',
    kpiMultiTenth: () => 'Tenth',
    kpiMultiOne: () => 'One',
    kpiMultiTen: () => 'Ten',
    kpiMultiHundred: () => 'Hundred',
    kpiMultiThousand: () => 'Thousand',
    kpiMultiTenthousand: () => 'Ten thousand',
    kpiType: () => 'KPI Type',
    kpiPeriodicityDaily: () => 'Daily',
    kpiPeriodicityMonthly: () => 'Monthly',
    kpiPeriodicityWeekly: () => 'Weekly',
    kpiPeriodicityYearly: () => 'Yearly',
    kpiTypeCounter: () => 'Counter',
    kpiTypeOxford: () => 'Oxford',
    kpiTypeRing: () => 'Ring',
    kpiTypeScale: () => 'Scale',
    kpiTypeSpeedometer: () => 'Speedometer',
    kpiTypeThermometer: () => 'Thermometer',
    kpiTypeTrafficLight: () => 'Traffic light',
    kpis: () => 'KPIs',
    java: () => 'Java',
    javascript: () => 'Javascript',
    jsp: () => 'JSP',
    keepExistingDefinition: () => 'Keep existing definition',
    label: () => 'Label',
    landingPage: () => 'Landing page',
    landingPageDefault: () => 'Default landing page',
    length: () => 'Length',
    localAttribute: () => 'Local attribute',
    localCardinality: () => 'Local cardinality',
    mask: () => 'Mask',
    mergeItems: (itemType: string) => parseTOKS(`Merge <TOK1>`, itemType),
    metadata: () => 'Metadata',
    model: () => 'Model',
    name: () => 'Name',
    naming: () => 'Naming',
    newEntityTitle: (entityTitle: string) =>
      parseTOKS(`New instance (<TOK1>)`, entityTitle),
    noImage: () => 'No image',
    noItemsToAutofill: () => 'There are no items to autofill',
    noDocsSelected: () => 'At least one doc must be selected',
    none: () => 'None',
    nonStrictGenerationWarning: () =>
      'You are about to start a non-strict generation. This might not be what you want. Are you sure you want to continue?',
    nothingToMerge: () =>
      'There are no similar elements in the selected collection',
    noSheetsSelected: () => 'At least one sheet must be selected',
    notIncluded: () => 'Not included',
    noTitleOnAddedElement: (type: string, name: string) =>
      parseTOKS(
        `Element "<TOK1>" of type "<TOK2>" must have a title`,
        name,
        type,
      ),
    number: () => 'Number',
    objects: () => 'Objects',
    objectsCount: (count: number | string) =>
      parseTOKS(`<TOK1> elements`, count),
    objectsNames: () => 'Objects names (comma separated)',
    objectType: () => 'Object type',
    objectDependencies: () => 'Item occurrences:',
    omit: () => 'Omit',
    omitEverything: () => 'Omit all',
    omittedItems: () => 'Omitted items',
    openCube: () => 'Open cube',
    openQuery: () => 'Open query',
    openWidgetDefinition: () => 'Open widget definition',
    options: () => 'Options',
    other: () => 'Other',
    outcome: () => 'Outcome',
    outcomes: () => 'Outcomes',
    palette: () => 'Palette',
    paletteBackgroundDefault: () => 'Background default',
    paletteBackgroundPaper: () => 'Background paper',
    palettePrimaryMain: () => 'Primary',
    palettePrimaryContrastText: () => 'Primary contrast text',
    paletteSecondaryMain: () => 'Secondary',
    paletteSecondaryContrastText: () => 'Secondary contrast text',
    paletteTextPrimary: () => 'Normal text',
    paletteTextTitle: () => 'Titles',
    panel: () => 'Panel',
    panels: () => 'Panels',
    parameters: () => 'Parameters',
    paste: () => 'Paste',
    path: () => 'Path',
    performer: () => 'Performer',
    performers: () => 'Performers',
    periodicity: () => 'Periodicity',
    pool: () => 'Pool',
    pools: () => 'Pools',
    position: () => 'Position',
    prefix: () => 'Prefix',
    prefixes: () => 'Prefixes',
    pricing: () => 'Pricing',
    pricingIncludedObjects: () => 'Included objects',
    prompt: () => 'Prompt',
    process: () => 'Process',
    processes: () => 'Processes',
    processEntity: (proName: string) =>
      `Main entity of the process <div>${proName}</div>`,
    processTask: () => 'Process task',
    processTasks: () => 'Process tasks',
    profile: () => 'Profile',
    profiles: () => 'Profiles',
    profileTotalHours: () => 'Profile total hours',
    project: () => 'Project',
    projectDetails: (projectName: string) => parseTOKS('<TOK1>', projectName),
    projectDocumentation: (projectName: string) =>
      parseTOKS(`<TOK1> documentation`, projectName),
    projectEstimate: (projectName: string) =>
      parseTOKS(`Estimate for the "<TOK1>" project.`, projectName),
    projectIcon: () => 'Project icon',
    projectName: () => 'Project name',
    projectPlan: () => 'Project plan',
    properties: () => 'Properties',
    proposal: () => 'Proposal',
    proposalCost: () => 'Proposal cost',
    proposalEffort: () => 'Proposal effort',
    proposalScope: () => 'Proposal scope',
    projectTypes: () => 'Proyect types',
    query: () => 'Query',
    queries: () => 'Queries',
    radio: () => 'Radio',
    readonly: () => 'Read-only',
    react: () => 'React',
    reference: () => 'Reference',
    references: () => 'References',
    refresh: () => 'Refresh',
    refreshOnFocus: () => 'Refresh on focus',
    regenerateEntity: () => 'Regenerate entity',
    regenerateForm: () => 'Regenerate form',
    regenerateProcess: () => 'Regenerate process',
    regexp: () => 'RegExp',
    relatedProcess: () => 'Related process',
    relatedProcesses: () => 'Related processes',
    relatedProcessesForProcess: (proName: string) =>
      `Processes related to <div>${proName}</div>`,
    removable: () => 'Removable',
    remove: () => 'Remove',
    removeElementTitle: (elementType: string, title: string) =>
      parseTOKS(`[<TOK1>] <TOK2>`, elementType, title),
    removeItem: (elementType: string, itemTitle: string) =>
      parseTOKS(`Remove <TOK1> <TOK2>`, elementType, itemTitle),
    removeSources: () => 'Remove sources',
    replaceExistingPrompt: () => 'Replace existing prompt',
    replaceWithNewElement: () => 'Replace with new definition',
    removeStateFromEntityWarning: () =>
      'Removing this status from the entity will also remove the status from all processes that contain it.',
    renameExistingProject: () => 'Rename existing element',
    renameConflictingElement: () => 'Rename conflicting element',
    required: () => 'Required',
    rest: () => 'Rest',
    result: () => 'Result',
    role: () => 'Role',
    roles: () => 'Roles',
    rolesAndActivities: () => 'Roles and Activities',
    rolesBlackBoxTester: () => 'Black Box Tester',
    rolesExternalFunctionalConsultant: () => 'External Functional Consultant',
    rolesFunctionalConsultant: () => 'Functional Consultant',
    rolesProjectManager: () => 'Project Manager',
    rolesSalesExecutive: () => 'Sales Executive',
    rolesServicesDirector: () => 'Services Director',
    rolesTechnicalSolutionArchitect: () => 'Technical Solution Architect',
    rolesTechnicalComponentsProgrammer: () => 'Technical Components Programmer',
    routine: () => 'Routine',
    rule: () => 'Rule',
    rules: () => 'Business rules',
    rulesForProcess: (proName: string) =>
      `Business rules for process <div>${proName}</div>`,
    schedule: () => 'Schedule',
    scheduleExcelTitle: () =>
      'Activities, Roles, Deliverables, and Processes Schedule',
    scheduleCollaboratorsAverage: () => 'Average collaborators needed',
    scheduleCollaboratorsMax: () => 'Max collaborators needed',
    scheduleCollaboratorsMin: () => 'Min collaborators needed',
    scheduleFromTo: (from: TId, to: TId) =>
      parseTOKS(`Day <TOK1> to day <TOK2>`, from, to),
    scheduleExplain: () =>
      "<p>Specify the amount of time in workdays you intend to spend on this project, and then detail the collaborators' involvement over time. This will directly determine the number of collaborators needed to meet the desired timeline.</p><p>Normally, a common project has a sixty workdays schedule.</p>",
    scheduleExplainPricing: () =>
      'Enter the hourly pricing for each role to calculate the total cost.',
    scheduleHoursCount: (count: TId) => parseTOKS(`<TOK1> hs`, count),
    schedulePeopleCount: (count: TId) => parseTOKS(`<TOK1> people`, count),
    schedulePeopleCountMin: (count: TId) => parseTOKS(`<TOK1>p`, count),
    schedulePeopleMax: (count: TId, from: TId, to: TId) =>
      parseTOKS(`<TOK1> people (Day <TOK2> to <TOK3>)`, count, from, to),
    schedulePeriod: () => 'Period',
    scheduleTitleCollaboratorsDetails: () => 'Weekly collaborator details',
    scheduleTitleCollaboratorsSummary: () => 'Collaborator summary',
    scheduleTitleDeliverablesSchedule: () => 'Deliverables schedule',
    scheduleTitleObjectsSchedule: () => 'Objects schedule',
    scheduleTitleDiagram: () => 'Activity Schedule Distribution',
    scheduleTitlePricing: () => 'Pricing',
    scope: () => 'Scope',
    search: () => 'Search',
    section: () => 'Section',
    sections: () => 'Sections',
    selectAll: () => 'Select all',
    selectWeek: () => 'Select week',
    showComments: () => 'Show comments',
    showHistory: () => 'Show history',
    showInformation: () => 'Show information',
    showRefresh: () => 'Show refresh',
    showDetailsForItem: (itemName: string) =>
      parseTOKS(`Show details for <TOK1>`, itemName),
    showInSiteMap: () => 'Show in sitemap',
    showValue: () => 'Show value',
    similaritiesFound: () => 'Similarities found',
    size: () => 'Size',
    soa: () => 'SOA',
    somethingWrongGenForms: () =>
      'Something went wrong when generating the form. Please, try again.',
    sourceItems: () => 'Source items',
    sortProcessTasks: (processTitle: string) =>
      parseTOKS(`Sort '<TOK1>' tasks`, processTitle),
    specsHTML: () => 'Specs html',
    srcName: () => 'Source name',
    state: () => 'State',
    states: () => 'States',
    statesForProcess: (proName: string) =>
      `States for process <div>${proName}</div>`,
    status: () => 'Status',
    string: () => 'String',
    structured: () => 'Structured',
    structuredExamples: () => 'Structured examples',
    suffixes: () => 'Suffixes',
    summary: () => 'Summary',
    summaryAttributesHours: () => 'Attributes',
    summaryBusinessClassesHours: () => 'Business classes',
    summaryDocumentsHours: () => 'Documents',
    summaryEntitiesHours: () => 'Entities',
    summaryExternalInterfacesHours: () => 'External interfaces',
    summaryFormsHours: () => 'Forms',
    summaryKpisHours: () => 'KPIs',
    summaryFixedCost: () => 'Fixed cost',
    summaryVariableCost: () => 'Variable cost',
    summaryProcessHours: () => 'Process',
    summarySubtotal: () => 'Subtotal',
    summaryFactors: () => 'Complexity factors',
    summaryTotal: () => 'Total',
    supervisor: () => 'Supervisor',
    tab: () => 'Show as tab',
    targetItem: () => 'Target item',
    task: () => 'Task',
    tasks: () => 'Tasks',
    tasksForProcess: (proName: string) =>
      `Tasks for process <div>${proName}</div>`,
    taskForProcess: (tskName: string, proName: string) =>
      `Task ${tskName} <div>for process ${proName}</div>`,
    template: () => 'Template',
    templates: () => 'Templates',
    textarea: () => 'Textarea',
    theme: () => 'Theme',
    threshold: () => 'Threshold',
    title: () => 'Title',
    tooMuchFields: () =>
      'The current prompt seems to contain too many fields. This might result in high usage costs. Are you sure you want to continue?',
    semiStructuredPromptRequired: () =>
      'The process cannot be generated until there is a valid semi-structured prompt. Generate or adjust the central prompt from the chat before continuing.',
    totalHours: () => 'Total hours',
    transcribeAudio: () => getLabel('lblAudioTranscription').text,
    trigger: () => 'Trigger',
    triggers: () => 'Triggers',
    type: () => 'Type',
    unnamedProject: () => 'Unnamed project',
    uploadFromDisk: () => 'Upload from disk',
    useCase: () => 'Use case',
    useCases: () => 'Use cases',
    useCasesForProcess: (proName: string) =>
      `Use cases of process <div>${proName}</div>`,
    user: () => 'User',
    users: () => 'Users',
    value: () => 'Value',
    view: () => 'View',
    visible: () => 'Visible',
    week: (no?: number) => parseTOKS(`Week No. <TOK1>`, no ?? ''),
    weekNo: () => 'Week No.',
    weekSummary: () => 'Week summary',
    widget: () => 'Widget',
    widgetPanel: () => 'Widget panel',
    widgets: () => 'Widgets',
    whyShouldFix: () =>
      'This button adjusts the introduced times to have a total of 1 by each role in each activity',
  },
  questions: {
    augmentProcessIntroduction: () =>
      `Please, introduce the augment personalization prompt below`,
    augmentProcessPrompt: (process: {
      name: string;
      title?: string;
      description?: string;
    }) =>
      parseTOKS(
        `<TOK1><TOK2>`,
        process.title ? `${process.title}\n\n` : '',
        process.description
          ? `Process description: ${process.description}`
          : '',
      ),
    cannotReadFile: () => 'The file cannot be read as project',
    cannotRemoveBecauseOfDependencies: () =>
      `Deleting this element will remove the following dependency paths as well. Do you wish to continue?:`,
    changesWillBeApplied: () =>
      'The project elements will be created in the current environment. This action is irreversible. Do you wish to continue?',
    changesWillBeLost: () => 'The changes will be lost',
    chooseExistingElement: (elementType: string) =>
      parseTOKS(`Choose <TOK1>`, elementType),
    cloneNewName: () => `Please, enter the name of the new element`,
    couldNotGetProcess: () => 'Something went wrong when obtaining the process',
    createItem: (itemName: string) => parseTOKS(`New <TOK1>`, itemName),
    doTheSameForEvery: (itemType: string) =>
      parseTOKS(`Do the same for every <TOK1>`, itemType),
    doThemSameForEverything: () => 'Do the same for everything',
    doYouWishToContinue: () => 'Do you wish to continue?',
    editForeignKeyConsequences: () =>
      'Editing a foreign key will remove all its instances. Do you wish to continue?',
    itemAlreadyAdded: (itemName: string) =>
      parseTOKS(
        `Item named "<TOK1>" has already been added to the project`,
        itemName,
      ),
    makeNewForeignKeyConsequences: () =>
      'Adding a new foreign key will remove all its instances. Do you wish to continue?',
    removeInstancesAttributesAsWell: () =>
      'Removing this attribute from the entity will remove the instances attribute values as well. Do you wish to continue?',
    removeOrphanElements: () => 'This action will remove these orphan items:',
    removeTheElement: () => `This action will delete the element`,
    removeWidgetConfirm: () =>
      'Do you want to remove this widget? This will also remove the autogenerated panels.',
    renameAlreadyUsedName: () =>
      `The chosen name is already taken. Please, choose another.`,
    thereIsNothingToRemove: () => 'There is nothing to remove',
    updateFieldClone: () => 'Clone and replace',
    updateFieldConfirm: () => 'Override',
    updateField: ({ TOK1 }: { TOK1: string }) =>
      parseTOKS(`Are you sure you want to update <TOK1>?`, TOK1),
    updateAffectedFields: () => 'This change will affect the following paths:',
  },
  tools: {
    addProject: 'Add definition to current project',
    advanced: 'Advanced',
    apply: 'Apply',
    autofill: 'Autofill',
    basic: 'Basic',
    close: 'Close',
    commandPalette: 'Command palette (Ctrl + Q)',
    configuration: 'Settings',
    collapse: 'Collapse all nodes',
    distance: 'Look for similar objects',
    document: 'Print documentation',
    export: 'Export',
    import: 'Import',
    intermediate: 'Intermediate',
    merge: 'Merge',
    new: 'New',
    notes: 'Notes (Ctrl+Shift+E)',
    open: 'Open',
    projectPlan: 'Project plan',
    redo: 'Redo',
    save: 'Save',
    saveConfiguration: 'Save configuration',
    saveDeliverables: 'Save deliverables',
    saveNaming: 'Save naming',
    saveParameters: 'Save parameters',
    saveRolesAndActivities: 'Save roles and activities',
    saveTemplates: 'Save templates',
    sort: 'Sort',
    sortByNameAsc: 'Sort by name ascending',
    sortByNameDesc: 'Sort by name descending',
    sortByTitleAsc: 'Sort by title ascending',
    sortByTitleDesc: 'Sort by title descending',
    undo: 'Undo',
    vacuum: 'Vacuum',
  },
});

